import { useMemo } from 'react';

import { State } from 'modules';

import { bindActionCreators, useDispatch, useSelector } from 'third-party';

import { DEFAULT_PAGE_SIZE } from 'constants/common';
import {
  changePasswordEndpoint,
  inviteUserEndpoint,
  userListEndpoint,
  userRightsEndpoint,
  userSettingsEndpoint,
  userSingleEndpoint,
} from 'constants/endpoints';

import { appSelectors } from 'selectors';

import { getPageNumberAfterLoadMore } from 'utils/helper';
import { httpMethod, simplifyBuilder } from 'utils/sra';

import {
  APISortOrder,
  APIUser,
  APIUserInvite,
  APIUserRightRequest,
  APIUserSearchFilter,
  APIUserSearchResult,
  APIUserSettingModel,
} from 'types/api';

export type UserState = {
  usersList: APIUser[];
  user: APIUser | null;
  loadUserPending: boolean;
  loadUserError: string | null;
  loadUsersPending: boolean;
  allUsersLoaded: boolean;
  inviteUserPending: boolean;
  usersListLoaded: boolean;
  filters: APIUserSearchFilter;
  pagination: {
    pageNumber: APIUserSearchResult['pageNumber'];
    pageSize: APIUserSearchResult['pageSize'];
  };
  userSettings: APIUserSettingModel[];
  userSettingsLoaded: boolean;
  loadUserSettingsPending: boolean;
  updateUserSettingPending: boolean;
  changeUserPasswordPending: boolean;
};

export const userState: UserState = {
  usersList: [],
  user: null,
  loadUserPending: false,
  loadUserError: null,
  filters: {
    searchSortOrders: [
      {
        sortField: 'Id',
        sortOrder: APISortOrder.DESC,
      },
    ],
  },
  inviteUserPending: false,
  loadUsersPending: false,
  usersListLoaded: false,
  allUsersLoaded: false,
  pagination: { pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE },
  userSettings: [],
  userSettingsLoaded: false,
  loadUserSettingsPending: false,
  updateUserSettingPending: false,
  changeUserPasswordPending: false,
};

const builder = simplifyBuilder(userState, {});

export const updateUserFilters = builder.createReduxAction(
  (filters: APIUserSearchFilter) => ({
    name: 'updateUserFilters',
    updater: () => ({
      filters,
    }),
  }),
);

export const loadUsers = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadUsers',
    url: userListEndpoint(organizationId),
    body: (state: State) => ({
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      ...state.user.filters,
    }),
    method: httpMethod.post,
    onSuccess: (state: UserState, payload: APIUserSearchResult) => ({
      usersList: payload.result,
      usersListLoaded: true,
      allUsersLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadMoreUsers = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadMoreUsers',
    url: userListEndpoint(organizationId),
    body: (state: State) => ({
      pageNumber: getPageNumberAfterLoadMore(
        state.user.usersList,
        state.user.pagination.pageSize,
      ),
      ...state.user.filters,
    }),
    method: httpMethod.post,
    onSuccess: (state: UserState, payload: APIUserSearchResult) => ({
      usersList: [...(state.usersList || []), ...payload.result],
      usersListLoaded: true,
      allUsersLoaded: payload.result.length < payload.pageSize,
      pagination: {
        pageNumber: payload.pageNumber,
        pageSize: payload.pageSize,
      },
    }),
  }),
);

export const loadUser = builder.createServerAction(
  (organizationId: string, userId: string) => ({
    name: 'loadUser',
    url: userSingleEndpoint(organizationId, userId),
    method: httpMethod.get,
    onSuccess: (state: UserState, payload: APIUser) => ({
      user: payload,
    }),
  }),
);

export const deleteUser = builder.createServerAction(
  (organizationId: string, userId: string) => ({
    name: 'deleteUser',
    url: userSingleEndpoint(organizationId, userId),
    method: httpMethod.delete,
    onSuccess: (state: UserState) => ({
      usersList: state.usersList.filter(user => user.id !== userId),
    }),
  }),
);

export const updateUser = builder.createServerAction(
  (organizationId: string, userId: string, model: APIUser) => ({
    name: 'updateUser',
    url: userSingleEndpoint(organizationId, userId),
    method: httpMethod.post,
    body: model,
    onSuccess: (state: UserState, result: APIUser) => ({
      usersList: state.usersList.map(user =>
        user.id === userId ? result : user,
      ),
    }),
  }),
);

export const createUser = builder.createServerAction(
  (organizationId: string, model: APIUser) => ({
    name: 'createUser',
    url: userListEndpoint(organizationId),
    method: httpMethod.put,
    body: { ...model },
    onSuccess: (state: UserState, result: APIUser) => ({
      usersList: [result, ...state.usersList],
    }),
  }),
);

export const inviteUser = builder.createServerAction(
  (organizationId: string, model: APIUserInvite) => ({
    name: 'inviteUser',
    url: inviteUserEndpoint(organizationId),
    method: httpMethod.put,
    body: { ...model },
    onSuccess: (state: UserState, result: APIUser) => ({
      usersList: [result, ...state.usersList],
    }),
  }),
);

export const updateUserRights = builder.createServerAction(
  (organizationId: string, userId: string, rights: APIUserRightRequest) => ({
    name: 'updateUserRights',
    url: userRightsEndpoint(organizationId, userId),
    method: httpMethod.post,
    body: rights,
    onSuccess: (state: UserState, result: APIUser) => ({
      usersList: state.usersList.map(user =>
        user.id === userId ? result : user,
      ),
      user: result,
    }),
  }),
);

export const loadUserSettings = builder.createServerAction(() => ({
  name: 'loadUserSettings',
  url: userSettingsEndpoint(),
  method: httpMethod.get,
  onSuccess: (state: UserState, payload: APIUserSettingModel[]) => ({
    userSettings: payload,
    userSettingsLoaded: true,
  }),
}));

export const updateUserSettings = builder.createServerAction(
  (setting: APIUserSettingModel) => ({
    name: 'updateUserSettings',
    url: userSettingsEndpoint(),
    method: httpMethod.put,
    body: setting,
    onSuccess: (state: UserState, result: APIUserSettingModel) => ({
      userSettings: state.userSettings.some(s => s.type === result.type)
        ? state.userSettings.map(s => (s.type === result.type ? result : s))
        : [...state.userSettings, result],
    }),
  }),
);

export const clearUser = builder.createReduxAction(() => ({
  name: 'clearUser',
  updater: () => ({
    user: null,
    // A start-request does not reset the error, so clear it here — otherwise a
    // failed load would keep showing on every later open.
    loadUserError: null,
  }),
}));

export const clearUserSettings = builder.createReduxAction(() => ({
  name: 'clearUserSettings',
  updater: () => ({
    userSettings: [] as APIUserSettingModel[],
    userSettingsLoaded: false,
  }),
}));

export const clearUserList = builder.createReduxAction(() => ({
  name: 'clearUserList',
  updater: () => ({
    usersList: [],
  }),
}));

export const clearUserFilters = builder.createReduxAction(() => ({
  name: 'clearUserFilters',
  updater: () => ({
    filters: userState.filters,
    pagination: userState.pagination,
  }),
}));

const changeUserPassword = builder.createServerAction(
  (currentPassword: string, newPassword: string) => ({
    name: 'changeUserPassword',
    url: changePasswordEndpoint(),
    method: httpMethod.post,
    body: { currentPassword, newPassword },
    onPending: (state: UserState) => ({
      ...state,
      changeUserPasswordPending: true,
    }),
    onSuccess: (state: UserState) => ({
      ...state,
      changeUserPasswordPending: false,
    }),
    onError: (state: UserState) => ({
      ...state,
      changeUserPasswordPending: false,
    }),
  }),
);

export const useUser = () => {
  const dispatch = useDispatch();
  const organizationId = useSelector(appSelectors.organizationId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          updateUserFilters,
          clearUser,
          clearUserFilters,
          clearUserList,
          changeUserPassword: changeUserPassword,
          inviteUser: inviteUser.bind(null, organizationId),
          updateUserRights: updateUserRights.bind(null, organizationId),
          createUser: createUser.bind(null, organizationId),
          updateUser: updateUser.bind(null, organizationId),
          deleteUser: deleteUser.bind(null, organizationId),
          loadUser: loadUser.bind(null, organizationId),
          loadUsers: loadUsers.bind(null, organizationId),
          loadUsersForOrganization: loadUsers,
          loadMoreUsers: loadMoreUsers.bind(null, organizationId),
          loadUserSettings,
          updateUserSettings,
          clearUserSettings,
        },
        dispatch,
      ),
    [dispatch, organizationId],
  );
};

export const userReducer = builder.getReducers();
